import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import Button from '../Button';
import Input from '../Inputs/Input';
import { StyledFormWrapper } from './styles/MyPagesForm.styling';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import {
  saveAddress,
  saveAlternativeAddress,
  savePassword,
  saveUserInfo,
  saveUsername,
} from '../../Actions/UserInfo.action';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import styled from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import Icon from '../Icon';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import { cells, grid } from '../../styling/utils/grid';
import Address from '../../Types/Address';
import EditorString from '../EditorString/EditorString';

const StyledGeneralMessage = styled.div`
  display: flex;
  align-items: center;
  color: ${getColor('alert')};
  margin-bottom: 1.6rem;
  ${text('medium')}

  span {
    margin-left: 0.4rem;
  }
`;

const StyledSuccessMessage = styled(StyledGeneralMessage)`
  color: ${getColor('success')};
  ${fontFamilyBold}
`;

const StyledZipCityWrapper = styled.div`
  ${grid}
  ${cells(2)}
`;

const MyPagesSettings = ({ introText }) => {
  const { current: userInfo, errors = {}, successMessage } = useTypedSelector(
    (state) => state.userInfo
  );
  const [address, setAddress] = useState(userInfo?.address);
  const [alternativeAddress, setAlternativeAddress] = useState(
    userInfo?.alternativeAddress
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userInfoForm, setUserInfoForm] = useState(userInfo);
  const [username, setUsername] = useState(userInfo?.username);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState();
  const dispatch = useTypedDispatch();
  const customerNumber = useTypedSelector(
    (state) => state.userInfo.current?.id
  );

  useEffect(() => {
    if (userInfo) {
      setUserInfoForm(userInfo);
      setAddress(userInfo?.address || undefined);
      setAlternativeAddress(userInfo?.alternativeAddress || undefined);
      setUsername(userInfo?.username || '');
    }
  }, [userInfo]);

  const setUserInfoValue = (key: string, value: any) => {
    if (!userInfoForm) return;

    const userInfoCopy = Object.assign({}, userInfoForm);
    userInfoCopy[key] = value;
    setUserInfoForm(userInfoCopy);
  };

  const setAddressValue = (key: string, value: string) => {
    if (!userInfo) return;
    const _address = { ...(address ?? userInfo.address) };
    _address[key] = value;
    setAddress(_address);
  };

  const setAlternativeAddressValue = (key: string, value: string) => {
    if (!userInfo) return;
    const _alternativeAddress = {
      ...(alternativeAddress ?? userInfo.alternativeAddress),
    };
    _alternativeAddress[key] = value;
    setAlternativeAddress(_alternativeAddress);
  };

  const submitUserInfo = (e: any) => {
    e.preventDefault();
    if (userInfoForm) {
      setIsLoading(true);
      dispatch(saveUserInfo(userInfoForm)).finally(() => setIsLoading(false));
    }
  };

  const submitUsername = (e: any) => {
    e.preventDefault();
    if (username) {
      setIsLoading(true);
      dispatch(saveUsername(username)).finally(() => setIsLoading(false));
    }
  };

  const submitChangePassword = (e: any) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError(translate('login.confirm-password.must-match'));
      return;
    } else {
      setConfirmPasswordError(undefined);
    }

    setIsLoading(true);
    dispatch(savePassword(password)).finally(() => setIsLoading(false));
  };

  const submitAddress = (e: any) => {
    e.preventDefault();
    if (address) {
      setIsLoading(true);
      dispatch(saveAddress(address)).finally(() => setIsLoading(false));
    }
  };

  const submitAlternativeAddress = (e: any) => {
    e.preventDefault();
    if (alternativeAddress) {
      setIsLoading(true);
      dispatch(saveAlternativeAddress(alternativeAddress)).finally(() =>
        setIsLoading(false)
      );
    }
  };

  const hasErrors = useCallback(
    (key: string) => errors[key] && errors[key]?.length,
    [errors]
  );
  const getErrorMessages = useCallback(
    (key: string) =>
      errors[key]?.map((err, idx) => <p key={key + err + idx}>{err}</p>),
    [errors]
  );

  useEffect(() => {
    if (errors['general']?.length || !!successMessage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errors, successMessage]);

  return (
    <StyledFormWrapper>
      <h1 className="title">{translate('mypage.info.title')}</h1>
      <div className="description">
        <EditorString value={introText.value} />
      </div>
      {hasErrors('general') ? (
        <StyledGeneralMessage>
          <Icon name="warning" />
          <span>{errors['general'] && errors['general'][0]}</span>
        </StyledGeneralMessage>
      ) : null}
      {successMessage ? (
        <StyledSuccessMessage>
          <Icon name="check" />
          <span>{successMessage}</span>
        </StyledSuccessMessage>
      ) : null}
      <form className="form form--settings">
        <Input
          inputSize="large"
          label={translate('mypage.person.firstname')}
          wrapperClassName="input__wrapper"
          placeholder={translate('general.placeholder.firstname')}
          onChange={(e) => setUserInfoValue('firstName', e.target.value)}
          type="text"
          id="fname"
          value={userInfoForm?.firstName ?? ''}
          disabled={isLoading}
          inputState={hasErrors('FirstName') ? 'error' : 'default'}
          message={getErrorMessages('FirstName')}
          required
        />
        <Input
          inputSize="large"
          label={translate('mypage.person.lastname')}
          wrapperClassName="input__wrapper"
          placeholder={translate('general.placeholder.lastname')}
          onChange={(e) => setUserInfoValue('lastName', e.target.value)}
          type="text"
          id="lname"
          value={userInfoForm?.lastName ?? ''}
          disabled={isLoading}
          required
        />
        <Input
          disabled={true}
          inputSize="large"
          label={translate('mypage.person.customernumber')}
          wrapperClassName="input__wrapper"
          type="text"
          id="cNumber"
          value={customerNumber}
        />
        <Input
          inputSize="large"
          label={translate('mypage.person.email')}
          wrapperClassName="input__wrapper"
          placeholder={translate('general.placeholder.email')}
          onChange={(e) => setUserInfoValue('email', e.target.value)}
          type="email"
          id="email"
          value={userInfoForm?.email ?? ''}
          disabled={isLoading}
          inputState={hasErrors('Email') ? 'error' : 'default'}
          message={getErrorMessages('Email')}
          required
        />
        <Input
          inputSize="large"
          label={translate('mypage.person.phone')}
          wrapperClassName="input__wrapper"
          placeholder={translate('general.placeholder.phone')}
          onChange={(e) => setUserInfoValue('phone', e.target.value)}
          type="tel"
          id="tel"
          value={userInfoForm?.phone ?? ''}
          disabled={isLoading}
          inputState={hasErrors('Phone') ? 'error' : 'default'}
          message={getErrorMessages('Phone')}
          required
        />
        <Button
          btnSize="large"
          btnType="primary"
          className="btn--settings"
          onClick={submitUserInfo}
          disabled={isLoading}
        >
          {translate('form.save-changes')}
        </Button>
      </form>
      {!userInfo?.isBusinessCustomer && (
        <form className="form form--settings">
          <h4 className="form__title">
            {translate('mypage.deliveryaddress.title')}
          </h4>
          <Input
            inputSize="large"
            label={translate('mypage.address.address')}
            wrapperClassName="input__wrapper"
            placeholder={translate('general.placeholder.address')}
            type="text"
            id="address"
            disabled={isLoading}
            value={address?.address1 ?? ''}
            onChange={(e) => setAddressValue('address1', e.target.value)}
            inputState={hasErrors('address') ? 'error' : 'default'}
            message={getErrorMessages('address')}
            required
          />
          <Input
            inputSize="large"
            label={translate('mypage.address.careof')}
            wrapperClassName="input__wrapper"
            placeholder={translate('general.placeholder.careof')}
            type="text"
            id="careOf"
            disabled={isLoading}
            value={address?.careOf ?? ''}
            onChange={(e) => setAddressValue('careOf', e.target.value)}
            inputState={hasErrors('CareOf') ? 'error' : 'default'}
            message={getErrorMessages('CareOf')}
          />
          <StyledZipCityWrapper>
            <Input
              inputSize="large"
              label={translate('mypage.address.zipcode')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.zipcode')}
              type="text"
              id="zipCode"
              disabled={isLoading}
              value={address?.zipCode ?? ''}
              onChange={(e) => setAddressValue('zipCode', e.target.value)}
              inputState={hasErrors('ZipCode') ? 'error' : 'default'}
              message={getErrorMessages('ZipCode')}
              required
            />
            <Input
              inputSize="large"
              label={translate('mypage.address.city')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.city')}
              type="text"
              id="city"
              disabled={isLoading}
              value={address?.city ?? ''}
              onChange={(e) => setAddressValue('city', e.target.value)}
              inputState={hasErrors('City') ? 'error' : 'default'}
              message={getErrorMessages('City')}
              required
            />
          </StyledZipCityWrapper>
          <Button
            btnSize="large"
            btnType="primary"
            className="btn--settings"
            disabled={isLoading}
            onClick={submitAddress}
          >
            {translate('mypage.address.save')}
          </Button>
        </form>
      )}
      {!userInfo?.isBusinessCustomer && (
        <form className="form form--settings">
          <h4 className="form__title">
            {translate('mypage.alternativeaddress.title')}
          </h4>
          <Input
            inputSize="large"
            label={translate('mypage.address.address')}
            wrapperClassName="input__wrapper"
            placeholder={translate('general.placeholder.address')}
            type="text"
            id="alternativeAddress"
            disabled={isLoading}
            value={alternativeAddress?.address1 ?? ''}
            onChange={(e) =>
              setAlternativeAddressValue('address1', e.target.value)
            }
            inputState={hasErrors('address') ? 'error' : 'default'}
            message={getErrorMessages('address')}
            required
          />
          <Input
            inputSize="large"
            label={translate('mypage.address.careof')}
            wrapperClassName="input__wrapper"
            placeholder={translate('general.placeholder.careof')}
            type="text"
            id="alternativeCareOf"
            disabled={isLoading}
            value={alternativeAddress?.careOf ?? ''}
            onChange={(e) =>
              setAlternativeAddressValue('careOf', e.target.value)
            }
            inputState={hasErrors('CareOf') ? 'error' : 'default'}
            message={getErrorMessages('CareOf')}
          />
          <StyledZipCityWrapper>
            <Input
              inputSize="large"
              label={translate('mypage.address.zipcode')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.zipcode')}
              type="text"
              id="alternativeZipCode"
              disabled={isLoading}
              value={alternativeAddress?.zipCode ?? ''}
              onChange={(e) =>
                setAlternativeAddressValue('zipCode', e.target.value)
              }
              inputState={hasErrors('ZipCode') ? 'error' : 'default'}
              message={getErrorMessages('ZipCode')}
              required
            />
            <Input
              inputSize="large"
              label={translate('mypage.address.city')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.city')}
              type="text"
              id="alternativeCity"
              disabled={isLoading}
              value={alternativeAddress?.city ?? ''}
              onChange={(e) =>
                setAlternativeAddressValue('city', e.target.value)
              }
              inputState={hasErrors('City') ? 'error' : 'default'}
              message={getErrorMessages('City')}
              required
            />
          </StyledZipCityWrapper>
          <Button
            btnSize="large"
            btnType="primary"
            className="btn--settings"
            onClick={submitAlternativeAddress}
            disabled={
              isLoading ||
              !alternativeAddress?.address1 ||
              !alternativeAddress?.zipCode ||
              !alternativeAddress?.city
            }
          >
            {translate('mypage.alternativeaddress.save')}
          </Button>
        </form>
      )}
      {userInfo?.mayEditLogin && (
        <>
          <form className="form form--settings">
            <Input
              inputSize="large"
              label={translate('form.new-password')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.password')}
              type="password"
              id="password"
              disabled={isLoading}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputState={hasErrors('password') ? 'error' : 'default'}
              message={getErrorMessages('password')}
              required
            />
            <Input
              inputSize="large"
              label={translate('login.confirm-password')}
              wrapperClassName="input__wrapper"
              placeholder={translate('general.placeholder.password')}
              type="password"
              id="confirmpassword"
              disabled={isLoading}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputState={confirmPasswordError ? 'error' : 'default'}
              message={confirmPasswordError}
              required
            />
            <Button
              btnSize="large"
              btnType="primary"
              className="btn--settings"
              disabled={isLoading || !password || !confirmPassword}
              onClick={submitChangePassword}
            >
              {translate('form.update-password')}
            </Button>
          </form>
        </>
      )}
    </StyledFormWrapper>
  );
};

export default MyPagesSettings;
